import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowCueStateV1 } from '@/types/player/perform-control-v1.types';
import type { SimulationCueSheetRow } from '@/types/player/simulation-cue-sheet.types';
import type { FlowControlSegmentKind } from '@/helpers/simulation-cue-engine.helper';
import { getObjectId } from '@/helpers/object.id.helper';
import {
  buildInBetweenPlayoutSegmentId,
  isInBetweenPlayoutPlayingInCueState,
  isInBetweenPlayoutSegmentKind,
  parseInBetweenPlayoutSegmentId,
  resolveInBetweenPlayoutFromCueState,
} from '@/helpers/flow-control-in-between-playout.helper';
import {
  FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID,
  FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID,
} from '@/components/flows/detail/slot-list/flow-slot-assets-inline-ids';
import type { FlowControlMediaExpandSource } from '@/state/stores/use-flow-control-media-expand-store';

function slotIdFromSegmentAnchor(anchor: string | undefined): string | undefined {
  if (!anchor?.startsWith('slot:')) {
    return undefined;
  }
  return anchor.slice('slot:'.length);
}

export function flowControlSlotIdsEqual(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) {
    return false;
  }
  const normalizedA = getObjectId(a) ?? a;
  const normalizedB = getObjectId(b) ?? b;
  return normalizedA === normalizedB;
}

/** Whether the inline media panel under a slot row should be open (manual expand or auto-follow playing). */
export function isFlowControlSlotMediaBelowVisible(
  slotId: string,
  expandId: string | null | undefined,
  playingExpandId: string | null | undefined,
): boolean {
  return flowControlSlotIdsEqual(expandId, slotId) || flowControlSlotIdsEqual(playingExpandId, slotId);
}

/** Active slot id for a row-bound inline media panel — always the row's slot, never a stale expand id from another segment. */
export function resolveFlowControlSlotMediaActiveId(
  slotId: string,
  expandId: string | null | undefined,
  playingExpandId: string | null | undefined,
  isSegmentPlaying: boolean,
): string | null {
  const visible =
    isSegmentPlaying || flowControlSlotIdsEqual(expandId, slotId) || flowControlSlotIdsEqual(playingExpandId, slotId);
  return visible ? slotId : null;
}

function isLiveCueStepActivelyPlaying(cueState: FlowCueStateV1): boolean {
  const step = cueState.currentStep;
  if (!step?.scope) {
    return false;
  }
  if (step.scope === 'pre_flow' || step.scope === 'after_flow') {
    return (
      Object.values(cueState.items ?? {}).some(
        (item) => item.scope === step.scope && item.phase === 'on_air' && item.isCurrent !== false,
      ) || cueState.currentStep?.scope === step.scope
    );
  }
  return (
    cueState.isSegmentRunning === true ||
    Object.values(cueState.items ?? {}).some(
      (item) =>
        item.isCurrent !== false &&
        (item.phase === 'on_air' ||
          item.phase === 'preloading' ||
          item.phase === 'preloaded' ||
          item.phase === 'loading_with_fallback'),
    )
  );
}

function resolveFlowControlExpandIdFromCurrentStep(cueState: FlowCueStateV1): string | null {
  const step = cueState.currentStep;
  if (!step?.scope) {
    return null;
  }
  const slotId = slotIdFromSegmentAnchor(step.segmentAnchor) ?? cueState.activeSlotId;
  if (step.scope === 'slot' && slotId) {
    return slotId;
  }
  if (step.scope === 'session' && slotId) {
    return slotId;
  }
  return resolveFlowControlExpandIdFromSegment(step.scope as FlowControlSegmentKind, slotId);
}

export function resolveFlowControlExpandIdFromSegment(
  segmentKind: string | null | undefined,
  slotId?: string,
  row?: SimulationCueSheetRow,
): string | null {
  if (!segmentKind) {
    return null;
  }
  switch (segmentKind as FlowControlSegmentKind) {
    case 'pre_flow':
      return FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID;
    case 'flow_intro':
      return FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID;
    case 'flow_outro':
      return FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID;
    case 'after_flow':
      return FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID;
    case 'session':
    case 'break':
      return slotId ?? row?.slotId ?? slotIdFromSegmentAnchor(row?.segmentAnchor) ?? null;
    case 'flow_transition':
    case 'flow_resume': {
      const resolvedSlotId = slotId ?? row?.slotId ?? slotIdFromSegmentAnchor(row?.segmentAnchor);
      if (!resolvedSlotId || !isInBetweenPlayoutSegmentKind(segmentKind)) {
        return null;
      }
      return buildInBetweenPlayoutSegmentId(segmentKind, resolvedSlotId);
    }
    default:
      return null;
  }
}

/** Expand id for the segment that owns a cue-sheet row (e.g. Next preview target). */
export function resolveFlowControlExpandIdFromCueRow(row: SimulationCueSheetRow | null | undefined): string | null {
  if (!row) {
    return null;
  }
  // Simulation / projection rows may set scope without segmentKind; prefer either.
  const segmentKind = row.segmentKind || row.scope;
  const slotId = row.slotId ?? slotIdFromSegmentAnchor(row.segmentAnchor);
  return resolveFlowControlExpandIdFromSegment(segmentKind, slotId, row);
}

/**
 * Whether a segment row should use the Next (look-ahead) fill.
 * Playing / on-air segment always keeps its error/info accent instead.
 */
export function isFlowControlNextCueSegmentRow(
  segmentExpandId: string | null | undefined,
  nextCueExpandId: string | null | undefined,
  isSegmentPlaying: boolean,
): boolean {
  if (isSegmentPlaying || !segmentExpandId || !nextCueExpandId) {
    return false;
  }
  if (flowControlSlotIdsEqual(segmentExpandId, nextCueExpandId)) {
    return true;
  }
  // In-between ids embed a slot id — compare kind + normalized slot when string equality fails.
  const segmentInBetween = parseInBetweenPlayoutSegmentId(segmentExpandId);
  const nextInBetween = parseInBetweenPlayoutSegmentId(nextCueExpandId);
  if (!segmentInBetween || !nextInBetween) {
    return false;
  }
  return (
    segmentInBetween.segmentKind === nextInBetween.segmentKind &&
    flowControlSlotIdsEqual(segmentInBetween.slotId, nextInBetween.slotId)
  );
}

export function isFlowControlSegmentPlayingLive(cueState: FlowCueStateV1 | undefined): boolean {
  if (!cueState) {
    return false;
  }
  if (!cueState.activeSegmentKind) {
    return isLiveCueStepActivelyPlaying(cueState);
  }
  const kind = cueState.activeSegmentKind;
  if (kind === 'pre_flow' || kind === 'after_flow') {
    return Object.values(cueState.items ?? {}).some((item) => item.phase === 'on_air' && item.isCurrent);
  }
  return (
    cueState.isSegmentRunning === true ||
    Object.values(cueState.items ?? {}).some((item) => item.phase === 'on_air' && item.isCurrent)
  );
}

export function resolveFlowControlPlayingExpandIdLive(cueState: FlowCueStateV1 | undefined): string | null {
  if (!cueState) {
    return null;
  }

  const inBetween = resolveInBetweenPlayoutFromCueState(cueState);
  if (inBetween && isInBetweenPlayoutPlayingInCueState(cueState, inBetween.segmentKind, inBetween.slotId)) {
    return resolveFlowControlExpandIdFromSegment(inBetween.segmentKind, inBetween.slotId);
  }

  const fromCurrentStep = resolveFlowControlExpandIdFromCurrentStep(cueState);
  if (fromCurrentStep && isLiveCueStepActivelyPlaying(cueState)) {
    return fromCurrentStep;
  }

  if (!isFlowControlSegmentPlayingLive(cueState)) {
    return null;
  }
  return resolveFlowControlExpandIdFromSegment(cueState.activeSegmentKind, cueState.activeSlotId);
}

export function resolveFlowControlPlayingExpandIdSimulation(
  activeSegmentKind: FlowControlSegmentKind | null,
  activeSegmentSlotId: string | undefined,
  selectedItemIds: string[],
  pointerRow: SimulationCueSheetRow | undefined,
): string | null {
  if (selectedItemIds.length === 0 || !activeSegmentKind) {
    return null;
  }
  return resolveFlowControlExpandIdFromSegment(activeSegmentKind, activeSegmentSlotId, pointerRow);
}

/** Manual browse uses expandId; transport and auto-follow prefer the playing segment. */
export function resolveFlowControlActiveExpandId(
  expandId: string | null | undefined,
  playingExpandId: string | null | undefined,
  selectedSlotId: string | null | undefined,
  expandSource: FlowControlMediaExpandSource = null,
): string | null {
  if (expandSource === 'manual' && expandId != null) {
    return expandId;
  }
  return playingExpandId ?? expandId ?? selectedSlotId ?? null;
}

export function findFlowSlotForControlExpandId(
  slots: FlowSlotDto[] | undefined,
  expandId: string,
): FlowSlotDto | undefined {
  if (!slots?.length) {
    return undefined;
  }
  const inBetween = parseInBetweenPlayoutSegmentId(expandId);
  if (inBetween) {
    return slots.find((slot) => slot.id === inBetween.slotId);
  }
  if (expandId.startsWith('__flow_')) {
    return undefined;
  }
  return slots.find((slot) => slot.id === expandId);
}

export function scrollFlowControlSegmentIntoView(expandId: string | null | undefined): void {
  if (!expandId) {
    return;
  }
  const el = document.querySelector(`[data-slot-id="${expandId}"]`);
  if (el && 'scrollIntoView' in el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}
